import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import api, { apiErrorMsg } from "../lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, KeyRound, CheckCircle, Sparkles } from "lucide-react";
import {
  FloatingLabelInput, PasswordInput, GradientButton, ContinueAsBanner,
  AuthSplitLeft, AuthHeader, AuthFooterNote, TrustBadgeStrip,
} from "../components/AuthUI";
import GoogleSignInButton from "../components/GoogleSignInButton";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.37 — Login page premium redesign.
 * Desktop: split-screen (dark hero left, glass auth card right).
 * Mobile: stacked, full-width, sticky-feel CTA.
 */
export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("password");  // v2.44 — password is primary; magic-link removed from UI per user feedback
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [remember, setRemember] = useState(true);

  usePageTitle("Welcome Back to HouseEntry");

  const queryNext = new URLSearchParams(loc.search).get("next") || "";

  const dashboardFor = (u) =>
    u.role === "admin" ? "/admin" :
    u.role === "dealer" ? "/dealer" :
    u.role === "builder" ? "/builder" :
    (u.role === "team" || u.role === "team_member") ? "/team" :
    "/";

  const continueAsExisting = () => {
    if (!user) return;
    nav(queryNext || dashboardFor(user));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      const next = queryNext || loc.state?.from || (
        u.role === "admin" ? "/admin" :
        u.role === "dealer" ? "/dealer" :
        u.role === "builder" ? "/builder" :
        u.role === "team" ? "/admin" :
        "/"
      );
      nav(next);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Login failed"));
    }
    setBusy(false);
  };

  const requestMagic = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const path = queryNext ? `/auth/magic-link?next=${encodeURIComponent(queryNext)}` : "/auth/magic-link";
      await api.post(path, { email });
      setMagicSent(true);
      toast.success("Sign-in link bhej diya — apna inbox check karein.");
    } catch (err) {
      toast.error(apiErrorMsg(err, "Could not send link"));
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-screen bg-zinc-50" data-testid="login-page">
      <AuthSplitLeft
        heading="Welcome Back to HouseEntry"
        sub="Access your dashboard, listings & smart lead system — all in one premium proptech workspace."
      />

      {/* Right: auth card */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <div className="auth-blobs lg:hidden opacity-30" />
        <div className="w-full max-w-md relative">
          <AuthHeader showBack backTo="/" />

          <div className="auth-glass rounded-2xl p-7 sm:p-8" data-testid="login-form">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#0055FF] mb-3 inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Secure Sign In
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-zinc-900">Welcome Back</h1>
            <p className="text-[14px] text-zinc-600 mt-1.5 mb-6">Access your dashboard, listings & smart lead system.</p>

            <ContinueAsBanner user={user} onContinue={continueAsExisting} />

            {mode === "password" && (
              <form onSubmit={submit} className="space-y-4">
                <FloatingLabelInput
                  id="login-email"
                  type="email"
                  label="Email address"
                  icon={Mail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  testid="login-email"
                />
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  testid="login-password"
                />

                <div className="flex items-center justify-between text-[12.5px]">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none text-zinc-700 font-medium">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-300 text-[#0055FF] focus:ring-[#0055FF]/30"
                      data-testid="login-remember"
                    />
                    Remember me
                  </label>
                  <Link to="/forgot-password" className="font-bold text-[#0055FF] hover:underline" data-testid="link-forgot">
                    Forgot password?
                  </Link>
                </div>

                <GradientButton type="submit" loading={busy} testid="login-submit" icon={ArrowRight}>
                  Access Dashboard
                </GradientButton>

                {/* Google Sign-In — Emergent-managed OAuth. See /app/auth_testing.md. */}
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center" aria-hidden>
                    <div className="w-full border-t border-zinc-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-[11px] uppercase tracking-widest font-semibold text-zinc-400">
                      or
                    </span>
                  </div>
                </div>
                <GoogleSignInButton testid="login-google-btn" label="Sign in with Google" />

                {/* v2.44 — Magic-link toggle hidden per user feedback. Keeping form logic intact for future. */}
              </form>
            )}

            {mode === "magic" && !magicSent && (
              <form onSubmit={requestMagic} className="space-y-4" data-testid="magic-form">
                <div className="bg-[#EEF4FF] border border-[#C7D9FF] rounded-xl p-3 text-[12.5px] text-[#0044CC] inline-flex items-start gap-2">
                  <KeyRound size={14} className="mt-0.5 shrink-0" />
                  <span>No password needed — we'll email a one-time secure link, valid 15 minutes.</span>
                </div>
                <FloatingLabelInput
                  id="magic-email"
                  type="email"
                  label="Email address"
                  icon={Mail}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  testid="magic-email"
                />
                <GradientButton type="submit" loading={busy} testid="magic-submit" icon={ArrowRight}>
                  Email me a sign-in link
                </GradientButton>
                <button
                  type="button"
                  onClick={() => setMode("password")}
                  className="w-full text-[12.5px] font-semibold text-zinc-600 hover:text-[#0055FF] inline-flex items-center justify-center gap-1.5"
                  data-testid="switch-to-password-btn"
                >
                  <ArrowLeft size={13} /> Use password instead
                </button>
              </form>
            )}

            {mode === "magic" && magicSent && (
              <div className="text-center py-6 space-y-4" data-testid="magic-sent">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="font-heading font-bold text-xl text-zinc-900">Check your inbox</h3>
                <p className="text-[13px] text-zinc-600">
                  Agar account exist karta hai, toh aapko ek sign-in link mil gaya hoga. Link 15 minute me expire ho jayega.
                </p>
                <button
                  onClick={() => { setMagicSent(false); setMode("magic"); }}
                  className="w-full h-11 rounded-xl border border-zinc-200 text-[13px] font-semibold text-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                  data-testid="magic-back-btn"
                >
                  Resend link
                </button>
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-zinc-200 text-center text-[12.5px] text-zinc-600">
              Need an account?{" "}
              <Link to="/register" className="text-[#0055FF] font-bold hover:underline" data-testid="link-register">Create one →</Link>
            </div>
            <div className="mt-2 text-center text-[11.5px] text-zinc-500">
              Team member? <Link to="/team-login" className="text-[#0055FF] font-bold hover:underline" data-testid="link-team-login">Sign in with mobile + OTP →</Link>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <TrustBadgeStrip items={["Verified", "Encrypted", "Trusted"]} />
            <AuthFooterNote />
          </div>
        </div>
      </div>
    </div>
  );
}
